// generic-client
import * as Sentry from '@sentry/react';
import { deviceTypes } from '@airtm/utils/dist/constants';
import fetch from 'unfetch';
import buildNotification from 'utils/buildNotification';
import i18n from 'i18next';
import { ApolloClient, InMemoryCache, ApolloLink, CombinedGraphQLErrors } from '@apollo/client';
import { map } from 'rxjs';

import { LocalState } from '@apollo/client/local-state';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { v4 as uuid } from 'uuid';

import { LOCAL_STORAGE, LOCAL_STORAGE_WHITELIST } from '@constants';
import JwtManager from 'lib/JwtManager';
import {
  initialData as initialNetworkStatus,
  networkStatusVar,
  writeNetworkStatus,
} from './clientSchema/NetworkStatus';
import {
  initialData as initialNotifications,
  readNotifications,
  writeNotifications,
} from './clientSchema/Notification';
import { getOrganizationPermissions } from './clientSchema/AuthUser';
import introspectionResult from './introspectionResult';
import { SentryLink } from './SentryLink/SentryLink';

const cache = new InMemoryCache({
  // https://www.graphql-code-generator.com/docs/plugins/fragment-matcher
  possibleTypes: introspectionResult.possibleTypes,
  typePolicies: {
    Crypto__Wallet: {
      keyFields: ['walletId'],
    },
    Numbers__Permission: {
      keyFields: ['userId', 'paymentMethodId'],
    },
    SecurityHub__Tier: {
      keyFields: ['userId'],
    },
    PaymentMethods__DirectFees: {
      keyFields: ['userId', 'paymentMethodId'],
    },
    Saasquatch__Promotion: {
      keyFields: ['promotionId'],
    },
    // By default, in the cache, the field data is completely replaced by the incoming one.
    // Apollo throws a warning if the merge function is not explicitily set for arrays and objects.
    PaymentMethods__SuggestedCategoriesResponse: {
      // Merge types of suggested payment methods (byTag, byCountry, recent)
      merge: true,
    },
    Auth__User: {
      fields: {
        organizationPermissions: {
          read(_, { readField }) {
            const organizationMember = readField('organizationMember');
            const role = organizationMember ? readField('role', organizationMember) : undefined;
            return getOrganizationPermissions(role);
          },
        },
      },
    },
    Query: {
      fields: {
        networkStatus: {
          read() {
            try {
              return networkStatusVar();
            } catch {
              return initialNetworkStatus;
            }
          },
        },
        notifications: {
          read() {
            return readNotifications();
          },
        },
        myPaginatedAirnerProfiles: {
          keyArgs: ['sortOn', 'filter'],
          merge(existing, incoming) {
            if (!incoming) return existing;
            return {
              ...incoming,
              ...(existing && {
                edges: [...existing.edges, ...incoming.edges],
              }),
            };
          },
        },
      },
    },
  },
});

const isDevEnv = process.env.NODE_ENV === 'development';

const OPERATIONS_WITH_HEADERS = ['UploadAttachment'];

const retryLink = new RetryLink({
  attempts: {
    max: Infinity,
    retryIf: (error, operation) => {
      const isQuery = operation.operationType === 'query';
      if (
        isQuery &&
        // retry gateway errors
        ([502, 504].includes(error.statusCode) ||
          // or changed ip auth error
          error.extensions?.reason === 'CHANGED_IP')
      ) {
        writeNetworkStatus({
          __typename: 'Local__NetworkStatus',
          online: true,
          retrying: true,
        });
        return true;
      }
      return false;
    },
  },
  delay: {
    initial: 300,
    jitter: true,
    max: 30000,
  },
});

const sentryLink = new SentryLink({
  setTransaction: true,
  setFingerprint: true,
  attachBreadcrumbs: {
    includeQuery: false,
    includeVariables: true,
    includeFetchResult: true,
    includeError: true,
    includeCache: false,
    includeContext: false,
    transform: (breadcrumb, operation) => {
      // remove sensible or redundant data response
      const isSensibleOperation = [
        'Login',
        'ChangePassword',
        'ConfirmPasswordReset',
        'SignUpPartial',
        'ConfirmMemberSignupMutation',
      ].includes(operation.operationName);

      if (isSensibleOperation) {
        return {
          ...breadcrumb,
          data: {
            ...breadcrumb.data,
            fetchResult: undefined,
            variables: undefined,
          },
        };
      }

      return breadcrumb;
    },
  },
});

const httpLink = new UploadHttpLink({
  credentials: 'include',
  fetch,
  uri: process.env.CORSOLA_URL,
});

const refererLink = new SetContextLink((prevContext) => {
  const { headers } = prevContext;
  return {
    headers: {
      ...headers,
      'X-Referer': window.location.href.split('?')[0],
    },
  };
});

const getDeviceType = () => {
  if (window.RENDER_ON_APP) {
    return window.DEVICE_TYPE || deviceTypes.ANDROID;
  }

  const isAndroid = /android/i.test(navigator.userAgent);
  // iPhone and iPad including iPadOS 13+ regardless of desktop mode settings
  // https://stackoverflow.com/a/9039885
  const isIOS =
    /iPhone|iPad/.test(navigator.userAgent) ||
    (/Mac/.test(navigator.userAgent) && 'ontouchend' in document);

  if (isAndroid) {
    return deviceTypes.ANDROID_WEB;
  }

  if (isIOS) {
    return deviceTypes.IOS_WEB;
  }

  return deviceTypes.DESKTOP;
};

const deviceTypeLink = new SetContextLink((prevContext) => {
  const { headers } = prevContext;
  return {
    headers: {
      ...headers,
      'X-Device-Type': getDeviceType(),
    },
  };
});

const webappNameLink = new SetContextLink((prevContext) => {
  const { headers } = prevContext;
  return {
    headers: {
      ...headers,
      'X-Webapp-Name': __APP_NAME__,
    },
  };
});

const langLink = new SetContextLink((prevContext) => {
  const { language } = i18n;
  const { headers } = prevContext;

  return {
    headers: {
      ...headers,
      'Accept-Language': language,
    },
  };
});

const authLink = new SetContextLink((prevContext) => {
  const { jwt } = JwtManager;
  const kountSessionid = localStorage.getItem(LOCAL_STORAGE.KOUNT_SESSIONID);
  const { headers } = prevContext;
  return {
    headers: {
      ...headers,
      'X-Kount-Session-Id': kountSessionid,
      authorization: jwt ? `Bearer ${jwt}` : '',
    },
  };
});

const networkStatusLink = new ApolloLink((operation, forward) => {
  return forward(operation).pipe(
    map((response) => {
      writeNetworkStatus({
        __typename: 'Local__NetworkStatus',
        online: true,
        retrying: false,
      });
      return response;
    }),
  );
});

const errorLink = new ErrorLink(({ error, operation, result }) => {
  const headers = OPERATIONS_WITH_HEADERS.includes(operation.operationName)
    ? operation.getContext()?.response?.headers
    : null;

  if (headers) {
    Object.assign(error, { headers });
  }

  if (CombinedGraphQLErrors.is(error)) {
    const notifications = readNotifications();
    error.errors.forEach((graphQLError) => {
      switch (graphQLError.extensions.code) {
        case 'JWT_SESSION_ERROR':
          JwtManager.destroyJwt();
          // Since the user is logged out, there's no need to pass the error up in the link chain
          if (result) result.errors = undefined;
          break;
        case 'UNAUTHENTICATED':
          JwtManager.destroyJwt();
          break;
        case 'RATE_LIMIT_ERROR': {
          const { id } = graphQLError.extensions;
          writeNotifications([
            ...notifications,
            buildNotification({
              autoClose: true,
              errorCode: graphQLError.extensions.errorNumber,
              message: i18n.t([
                `ERRORS:RATE_LIMIT_ERROR_MESSAGE_${id}`,
                'ERRORS:RATE_LIMIT_ERROR_MESSAGE',
              ]),
              title: i18n.t([
                `ERRORS:RATE_LIMIT_ERROR_TITLE_${id}`,
                'ERRORS:RATE_LIMIT_ERROR_TITLE',
              ]),
              type: 'informative',
            }),
          ]);
          break;
        }
        default:
          if (
            !graphQLError?.extensions?.errorNumber &&
            graphQLError?.extensions?.code === 'INTERNAL_SERVER_ERROR' &&
            (graphQLError?.extensions?.response?.status || 500) >= 500
          ) {
            Sentry.addBreadcrumb({
              category: 'graphql',
              data: {
                operation: operation.operationName,
                error: JSON.stringify(graphQLError, null, 2),
                query: operation.query.loc?.source?.body,
                variables: JSON.stringify(operation.variables, null, 2),
              },
              level: 'error',
              message: graphQLError.message || 'An unexpected error occurred',
            });
          }
          break;
      }
    });
  } else {
    Sentry.addBreadcrumb({
      category: error.name,
      data: {
        [error.name]: JSON.stringify(error, null, 2),
        operation: operation.operationName,
        query: operation.query.loc?.source?.body,
        variables: JSON.stringify(operation.variables, null, 2),
      },
      level: 'error',
      message: `[${error.name}]: ${error.message}`,
    });
    if (!error.statusCode) {
      writeNetworkStatus({
        __typename: 'Local__NetworkStatus',
        online: false,
        retrying: false,
      });
    }
  }
});

const eventIdLink = new SetContextLink((prevContext) => {
  const { headers } = prevContext;
  return {
    headers: {
      ...headers,
      'X-Event-Id': uuid(),
    },
  };
});

export const link = ApolloLink.from([
  sentryLink,
  retryLink,
  errorLink,
  authLink,
  langLink,
  deviceTypeLink,
  webappNameLink,
  refererLink,
  eventIdLink,
  networkStatusLink,
  httpLink,
]);

const localState = new LocalState();

const client = new ApolloClient({
  clientAwareness: {
    name: 'web',
  },
  cache,
  defaultOptions: {
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
    watchQuery: {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
    },
  },
  link,
  localState,
  devtools: {
    enabled: isDevEnv,
  },
});

const initializeData = () => {
  writeNetworkStatus(initialNetworkStatus);
  writeNotifications(initialNotifications);
};

initializeData();

// on reset apollo cache, we clear local and session storages
client.onClearStore(() => {
  sessionStorage.clear();
  Object.keys(localStorage).forEach((key) => {
    if (!LOCAL_STORAGE_WHITELIST.includes(key)) {
      localStorage.removeItem(key);
    }
  });
  initializeData();
});

// if jwt is expired, reset apollo cache
const onJwtUpdated = (jwt) => {
  if (!jwt) client.clearStore();
};

JwtManager.addJwtListener(onJwtUpdated);

export default client;
